import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Dumbbell, Users, Shield, Trophy, MapPin, Calendar, ArrowRight, CheckCircle2, Star } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const programs = [{
  title: 'Brazilian Jiu-Jitsu',
  description: 'Master the art of ground fighting with our comprehensive BJJ program for all levels.',
  link: '/programs/brazilian-jiu-jitsu',
  icon: <Users className="w-6 h-6" data-id="element-223" />
}, {
  title: 'Kids Brazilian Jiu-Jitsu',
  description: 'Build confidence, discipline, and respect in a safe and fun environment for children.',
  link: '/programs/kids-brazilian-jiu-jitsu',
  icon: <Star className="w-6 h-6" data-id="element-224" />
}, {
  title: 'Muay Thai',
  description: 'Learn the "Art of Eight Limbs" with high-energy striking and clinching techniques.',
  link: '/programs/muay-thai',
  icon: <Dumbbell className="w-6 h-6" data-id="element-225" />
}, {
  title: 'Boxing',
  description: 'Develop superior hand speed, footwork, and defensive skills in our boxing classes.',
  link: '/programs/boxing',
  icon: <Trophy className="w-6 h-6" data-id="element-226" />
}, {
  title: 'Kickboxing',
  description: 'Combine boxing punches with martial arts kicks for a full-body workout and self-defense.',
  link: '/programs/kickboxing',
  icon: <Shield className="w-6 h-6" data-id="element-227" />
}, {
  title: 'MMA Training',
  description: 'Integrate striking and grappling skills for complete mixed martial arts proficiency.',
  link: '/programs/mma-training',
  icon: <Users className="w-6 h-6" data-id="element-228" />
}, {
  title: 'Personal Training',
  description: 'Achieve your specific fitness goals with one-on-one coaching from expert trainers.',
  link: '/programs/personal-training',
  icon: <Dumbbell className="w-6 h-6" data-id="element-229" />
}, {
  title: 'Adopt A Cop',
  description: 'Specialized training and support for active law enforcement officers at discounted rates.',
  link: '/programs',
  icon: <Shield className="w-6 h-6" data-id="element-230" />
}];
const cities = ['Redford', 'Livonia', 'Detroit', 'Farmington Hills', 'Southfield', 'Dearborn Heights', 'Westland', 'Garden City', 'Oak Park', 'Beverly Hills', 'Farmington', 'Dearborn', 'Novi', 'Northville', 'Plymouth', 'Royal Oak', 'Ferndale', 'Inkster'];
export function Home() {
  return <div className="flex flex-col w-full" data-id="element-231">
      {/* SECTION 1: HERO */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center bg-[#0f0f0f] text-white overflow-hidden" data-id="element-232">
        {/* Abstract Background */}
        <div className="absolute inset-0 z-0" data-id="element-233">
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40 z-10" data-id="element-234" />
          <img src="https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80" alt="Martial Arts Background" className="w-full h-full object-cover opacity-50" data-id="element-235" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-236">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="space-y-8" data-id="element-237">
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight uppercase italic" data-id="element-238">
              <span className="text-red-600" data-id="element-239">Train</span> With Purpose
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto font-light" data-id="element-240">
              Metro Detroit's Premier MMA, BJJ, and Fitness Training Center.
              Beginner friendly, competition ready.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8" data-id="element-241">
              <Link to="/free-class" className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-md uppercase tracking-wider transition-all transform hover:scale-105 shadow-lg" data-id="element-242">
                Try a Free Class
              </Link>
              <Link to="/schedule" className="px-8 py-4 bg-transparent border-2 border-white hover:bg-white hover:text-gray-900 text-white font-bold rounded-md uppercase tracking-wider transition-all" data-id="element-243">
                View Class Schedule
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: PROGRAMS / CLASSES */}
      <section className="py-20 bg-white" data-id="element-244">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-245">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-246">
            <h2 className="text-4xl font-bold text-gray-900 uppercase mb-4" data-id="element-247">
              Our Programs
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto" data-id="element-248"></div>
            <p className="mt-4 text-xl text-gray-600" data-id="element-249">
              World-class training for every goal and experience level.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-250">
            {programs.map((program, index) => <motion.div key={index} variants={fadeInUp} data-id="element-251">
                <Link to={program.link} className="group block h-full" data-id="element-252">
                  <div className="h-full p-8 bg-gray-50 rounded-xl border border-gray-100 hover:border-red-200 hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1" data-id="element-253">
                    <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mb-6 group-hover:bg-red-600 group-hover:text-white transition-colors" data-id="element-254">
                      {program.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors" data-id="element-255">
                      {program.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-256">
                      {program.description}
                    </p>
                  </div>
                </Link>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: WHO WE TRAIN */}
      <section className="py-20 bg-[#0f0f0f] text-white" data-id="element-257">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-258">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-259">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-260">
              <h2 className="text-4xl font-bold uppercase mb-6" data-id="element-261">
                Who We Train
              </h2>
              <div className="w-24 h-1 bg-red-600 mb-8" data-id="element-262"></div>
              <p className="text-xl text-gray-300 mb-8" data-id="element-263">
                At Mash Gym, we believe martial arts is for everyone. Whether
                you're stepping onto the mats for the first time or preparing
                for a professional fight, our doors are open.
              </p>

              <ul className="space-y-4" data-id="element-264">
                {['Beginners with absolutely no prior experience', 'Adults looking to improve fitness and learn self-defense', 'Competitive athletes and professional fighters', 'Kids and teens building confidence and discipline', 'Active law enforcement officers seeking practical skills'].map((item, i) => <li key={i} className="flex items-start" data-id="element-265">
                    <CheckCircle2 className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" data-id="element-266" />
                    <span className="text-lg text-gray-200" data-id="element-267">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl" data-id="element-268">
              <img src="https://images.unsplash.com/photo-1555597673-b21d5c935865?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80" alt="Diverse group training" className="absolute inset-0 w-full h-full object-cover" data-id="element-269" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-270">
                <div className="absolute bottom-8 left-8" data-id="element-271">
                  <p className="text-2xl font-bold text-white" data-id="element-272">
                    All Experience Levels Welcome
                  </p>
                  <p className="text-gray-300" data-id="element-273">
                    Join a community that supports your growth.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4: WHY TRAIN AT MASH GYM */}
      <section className="py-20 bg-gray-50" data-id="element-274">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-275">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-276">
            <h2 className="text-4xl font-bold text-gray-900 uppercase mb-4" data-id="element-277">
              Why Choose Mash Gym?
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto" data-id="element-278"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-279">
            {[{
            title: 'Expert Coaching',
            desc: 'Learn from experienced instructors who are dedicated to your progress, not their own ego.'
          }, {
            title: 'Structured Curriculum',
            desc: 'Follow a proven training path designed to take you from basics to advanced techniques safely.'
          }, {
            title: 'Positive Culture',
            desc: 'Train in a supportive, respectful environment where teammates help each other succeed.'
          }].map((benefit, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-white p-8 rounded-xl shadow-sm border-b-4 border-red-600" data-id="element-280">
                <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-281">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-lg" data-id="element-282">{benefit.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 5: FACILITY */}
      <section className="py-20 bg-white" data-id="element-283">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-284">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-285">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="order-2 lg:order-1" data-id="element-286">
              <div className="grid grid-cols-2 gap-4" data-id="element-287">
                <img src="https://images.unsplash.com/photo-1595078475328-1ab05d0a6a0e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Training Mats" className="rounded-lg shadow-lg w-full h-48 object-cover" data-id="element-288" />
                <img src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Equipment" className="rounded-lg shadow-lg w-full h-48 object-cover mt-8" data-id="element-289" />
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="order-1 lg:order-2" data-id="element-290">
              <h2 className="text-4xl font-bold text-gray-900 uppercase mb-6" data-id="element-291">
                World-Class Facility
              </h2>
              <div className="w-24 h-1 bg-red-600 mb-8" data-id="element-292"></div>
              <p className="text-lg text-gray-600 mb-6" data-id="element-293">
                Our facility is purpose-built for martial arts excellence. We
                provide a clean, safe, and organized environment where you can
                focus entirely on your training.
              </p>
              <p className="text-lg text-gray-600 mb-6" data-id="element-294">
                Featuring spacious mat areas for grappling, dedicated zones for
                striking and heavy bag work, and conditioning equipment to get
                you in peak fighting shape.
              </p>
              <p className="text-lg text-gray-600" data-id="element-295">
                We take pride in maintaining a professional atmosphere that is
                welcoming to families, professionals, and serious athletes
                alike.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 6: COMMUNITY */}
      <section className="py-20 bg-[#0f0f0f] text-white text-center" data-id="element-296">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-297">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-298">
            <Users className="w-16 h-16 text-red-600 mx-auto mb-6" data-id="element-299" />
            <h2 className="text-4xl font-bold uppercase mb-6" data-id="element-300">
              More Than Just A Gym
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-301">
              Mash Gym is a community. We foster a culture of respect,
              discipline, and accountability. Our coaches are actively involved
              in your journey, and your teammates become your support system.
              Whether you're a law enforcement officer, a parent, or a student,
              you'll find a welcoming home here.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 7: AREAS WE SERVE */}
      <section className="relative py-24 flex items-center justify-center min-h-[600px]" data-id="element-302">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-303">
          <div className="absolute inset-0 bg-[#0f0f0f]/80 z-10" data-id="element-304" />
          <img src="https://cdn.magicpatterns.com/uploads/7erwQ61hLFSB4s5oMY5XR9/Areas-We-Serve-BG-Image.png" alt="Metro Detroit Map" className="w-full h-full object-cover" data-id="element-305" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-306">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-307">
            <h2 className="text-4xl font-bold text-white uppercase mb-4" data-id="element-308">
              Areas We Serve
            </h2>
            <p className="text-xl text-red-500 font-semibold tracking-wide uppercase" data-id="element-309">
              Metro Detroit Area
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="flex flex-wrap justify-center gap-3" data-id="element-310">
            {cities.map((city, index) => <motion.div key={index} variants={fadeInUp} data-id="element-311">
                <Link to={`/cities/${city.toLowerCase().replace(/\s+/g, '-')}`} className="inline-block px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-red-600 hover:border-red-600 text-white rounded-full transition-all duration-300 text-sm font-medium tracking-wide" data-id="element-312">
                  {city}
                </Link>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* SECTION 8: SCHEDULE PREVIEW */}
      <section className="py-20 bg-white" data-id="element-313">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-314">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-gray-50 rounded-2xl p-12 border border-gray-100 shadow-lg" data-id="element-315">
            <Calendar className="w-12 h-12 text-red-600 mx-auto mb-6" data-id="element-316" />
            <h2 className="text-3xl font-bold text-gray-900 uppercase mb-4" data-id="element-317">
              Ready to Train?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-318">
              We offer a structured weekly schedule with classes available for
              all age groups and experience levels. Getting started is easy.
            </p>
            <Link to="/schedule" className="inline-flex items-center px-8 py-4 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-md uppercase tracking-wider transition-all" data-id="element-319">
              View Full Schedule
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-320" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SECTION 9: FINAL CTA */}
      <section className="py-24 bg-red-600 text-white text-center" data-id="element-321">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-322">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-323">
            <h2 className="text-4xl md:text-5xl font-extrabold uppercase mb-6" data-id="element-324">
              Start Your Journey Today
            </h2>
            <p className="text-xl text-red-100 mb-10 max-w-2xl mx-auto" data-id="element-325">
              Don't wait to get in the best shape of your life. Join the Mash
              Gym family and discover what you're capable of.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-326">
              <Link to="/free-class" className="px-10 py-5 bg-white text-red-600 font-bold rounded-md uppercase tracking-wider hover:bg-gray-100 transition-all shadow-xl" data-id="element-327">
                Try a Free Class
              </Link>
              <Link to="/contact" className="px-10 py-5 bg-red-700 border-2 border-red-500 text-white font-bold rounded-md uppercase tracking-wider hover:bg-red-800 transition-all" data-id="element-328">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}